import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import {
  CourseModel,
  DashboardModel,
  LessonModel,
} from '@generativ/much-smarter-models';
import { DashboardService } from './dashboard.service';
import { FeedbackService } from '../../core/feedback/feedback.service';
import { ContentfulService } from '../../shared/contentful.service';
import { CourseService } from '../../shared/course.service';
import {
  ContentfulAssetType,
  DashboardType,
  EntryDictionary,
} from '@generativ/ms-student-types';
import { StudentDashboardStatsModel } from '@generativ/much-smarter-models';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { NgStyle, DecimalPipe } from '@angular/common';
import { NgbNav as NgbNav_1, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap/nav';
import { DashboardSubjectComponent } from './dashboard-subject/dashboard-subject.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { TimePipe } from '../../shared/pipes/time.pipe';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgStyle, NgbNav_1, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, DashboardSubjectComponent, SpinnerComponent, NgbNavOutlet, DecimalPipe, TimePipe]
})
export class DashboardComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  info: Observable<DashboardModel>;
  content: DashboardType;
  activeCourse: CourseModel;
  lessons: LessonModel[];
  dashboardStats: StudentDashboardStatsModel;
  courseProgressPercentage = 0;
  lastExerciseAccuracy = 0;
  lastExerciseAverageTime = 0;
  lifetimeAccuracy = 0;
  lifetimeAverageTime = 0;
  persistenceRounds = 0;
  exercisesRepeated = 0;
  @ViewChild('t') tabset: NgbNav;

  constructor(
    private dashboardService: DashboardService,
    private feedbackService: FeedbackService,
    private contentfulService: ContentfulService,
    private courseService: CourseService
  ) {}

  ngOnInit() {
    // this.info = this.dashboardService.getDashboardInfo();
    this.contentfulService
      .getStudentEntry(EntryDictionary.Dashboard.dashboard)
      .then((entry) => {
        this.content = new DashboardType(entry);
      });
    this.subscriptions.add(
      this.courseService.activeCourse.subscribe((course) => {
        this.activeCourse = course;
        if (course && course.subject) {
          this.getSubjectStats();
          const firstSubject = this.activeCourse.subject.split(',')[0];
          this.getSubjectLessons(firstSubject);
        }
      })
    );
  }

  onSelectSubject(event) {
    this.getSubjectLessons(event.nextId);
  }

  getSubjectStats() {
    this.subscriptions.add(
      this.dashboardService
        .getStudentDashboardStats(this.activeCourse.id)
        .subscribe((stats) => {
          this.dashboardStats = stats;
          this.courseProgressPercentage = stats.courseProgress * 100;
          if (
            stats.lastExerciseStats &&
            stats.lastExerciseStats.problemsAttempted
          ) {
            this.lastExerciseAccuracy =
              (stats.lastExerciseStats.problemsCorrect /
                stats.lastExerciseStats.problemsAttempted) *
              100;
            this.lastExerciseAverageTime =
              stats.lastExerciseStats.problemTimeTotal /
              stats.lastExerciseStats.problemsAttempted;
          }
          if (stats.lifetimeStats && stats.lifetimeStats.problemsAttempted) {
            this.lifetimeAccuracy =
              (stats.lifetimeStats.problemsCorrect /
                stats.lifetimeStats.problemsAttempted) *
              100;
            this.lifetimeAverageTime =
              stats.lifetimeStats.problemTimeTotal /
              stats.lifetimeStats.problemsAttempted;
          }
          this.persistenceRounds = stats.lifetimeStats?.persistenceRounds ?? 0;
          this.exercisesRepeated = stats.lifetimeStats?.exercisesRepeated ?? 0;
        })
    );
  }

  getSubjectLessons(subject: string) {
    this.subscriptions.add(
      this.dashboardService
        .getLessonBySubjects(this.activeCourse.id, subject)
        .subscribe((lessons) => {
          this.lessons = lessons;
        })
    );
  }

  transformSubjectsToList(subjects: string): string[] {
    return subjects.split(',');
  }

  getVideoBasedOnSubject(subject: string): any {
    const defaultSubjectVideoName =
      subject.split(' ').reduce((acc, word, i) => {
        if (i === 0) {
          return word.toLowerCase();
        } else {
          return [...acc, word[0].toUpperCase() + word.slice(1).toLowerCase()];
        }
      }, []) + 'Video';
    switch (subject) {
      case 'MyGame':
        return this.content.mindsetVideo;
      case 'ACT English':
        return this.content.englishVideo;
      case 'ACT Reading':
        return this.content.readingVideo;
      case 'ACT Math':
        return this.content.mathVideo;
      case 'ACT Science':
        return this.content.scienceVideo;
      case 'GCSE English':
        return this.content.gcseEnglishVideo;
      case 'GCSE Maths':
        return this.content.gcseMathsVideo;
      case 'GCSE Science':
        return this.content.gcseMathsVideo;
      case 'SAT English':
        return this.content.satEnglishVideo;
      case 'SAT Math':
        return this.content.satMathVideo;
      case 'SAT Reading':
        return this.content.satReadingVideo;
      case 'SAT Writing':
        return this.content.satWritingVideo;
      default:
        const newCustomObject: ContentfulAssetType = {
          fields: this.content.fields[defaultSubjectVideoName].fields,
          file: this.content.fields[defaultSubjectVideoName].fields.file,
          sys: this.content.fields[defaultSubjectVideoName].sys,
          title: this.content.fields[defaultSubjectVideoName].fields.title,
        };
        return newCustomObject || null;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
